import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import HeroSection from './HeroSection';
import TestimonialSection from './TestimonialSection';
import Porti from './port';
import ClientFeedback from './ClientFeedback';
import ContactForm from './Contact/ContactForm';
import { useInView } from 'react-intersection-observer';

const Section = ({ children, id, className = '' }) => {
  const { ref, inView } = useInView({ 
    threshold: 0.1,
    triggerOnce: true 
  });

  return (
    <section
      id={id}
      ref={ref}
      className={`relative ${className} ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      } transition-all duration-700 ease-out`}
    >
      {children}
    </section>
  );
};

const FloatingNav = ({ onNavClick, activeSection }) => {
  const navItems = [
    { id: 'hero', label: 'Home', icon: '🏠' },
    { id: 'portfolio', label: 'Portfolio', icon: '💼' },
    { id: 'feedback', label: 'Feedback', icon: '💬' },
    { id: 'testimonials', label: 'Testimonials', icon: '⭐' },
    { id: 'contact', label: 'Contact', icon: '📞' },
  ];

  const handleClick = (id) => {
    onNavClick(id);
  };

  return (
    <nav className="fixed right-6 top-1/2 transform -translate-y-1/2 z-40 hidden lg:block">
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-3 shadow-lg border border-gray-200 dark:border-gray-700">
        <ul className="space-y-4">
          {navItems.map((item) => (
            <li key={item.id}>
              <button
                onClick={() => handleClick(item.id)}
                className={`p-2 rounded-xl transition-all duration-300 ${
                  activeSection === item.id 
                    ? 'bg-[#FD5F20] text-white shadow-md' 
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
                aria-label={`Go to ${item.label}`}
                title={item.label}
              >
                <span className="text-lg">{item.icon}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
};

const Home = () => {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [isFormActive, setIsFormActive] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const mainContentRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1200);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['hero', 'portfolio', 'feedback', 'testimonials', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element && scrollPosition >= element.offsetTop) {
          setActiveSection(section);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  const handleFormFocus = () => {
    setIsFormActive(true);
  };

  const handleFormBlur = () => {
    setIsFormActive(false);
  };

  return (
    <>
      {isLoading ? (
        <div className="fixed inset-0 bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 flex items-center justify-center z-50">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full border-4 border-[#FD5F20]/20 border-t-[#FD5F20] animate-spin "></div>
            <p className="text-gray-600 dark:text-gray-300 font-medium">Loading...</p>
          </div>
        </div>
      ) : (
        <>
          <FloatingNav onNavClick={handleNavClick} activeSection={activeSection} />
    
          <div 
            className="min-h-screen bg-background text-foreground relative"
            ref={mainContentRef}
          >
            {/* Hero Section - No extra padding/margin */}
            <Section id="hero" className="min-h-screen">
              <HeroSection 
                onExplore={() => handleNavClick('portfolio')}
                onFormFocus={handleFormFocus}
                onFormBlur={handleFormBlur}
              />
            </Section>

            {/* Main Content Sections */}
            <div className="relative z-0">
              <Section 
                id="portfolio" 
                className="min-h-screen"
              >
                <Porti />
              </Section>
              
              <Section id="feedback">
                <ClientFeedback />
              </Section>
              
              <Section id="testimonials">
                <TestimonialSection />
              </Section>
              
              <Section id="contact">
                <ContactForm />
              </Section>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Home;